// SubscriptionContext.jsx
// Handles state and actions for subscriptions across the app
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getCurrentSubscription, 
  getAvailablePlans, 
  subscribeToPlan, 
  validateCoupon, 
  cancelSubscription,
  renewSubscription,
  requestRefund,
  getSubscriptionHistory,
  getSubscriptionStatus,
  comparePlans,
  // These would need to be implemented in your subscriptions.js API
  // getPaymentMethods,
  // updateSubscriptionAutoRenewal
} from '../api/subscriptions';

// Create the context
const SubscriptionContext = createContext();

// Custom hook for using the subscription context
export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

// Provider component
export const SubscriptionProvider = ({ children }) => {
  // State
  const [currentPlan, setCurrentPlan] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [couponData, setCouponData] = useState(null);
  const [isSubscriptionExpiring, setIsSubscriptionExpiring] = useState(false);
  // const [paymentMethods, setPaymentMethods] = useState([]);
  const [subscriptionHistory, setSubscriptionHistory] = useState([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  // Fetch current subscription on mount
  useEffect(() => {
    fetchCurrentSubscription();
    fetchAvailablePlans();
  }, []);

  // Check if subscription is expiring soon
  useEffect(() => {
    if (activeSubscription) {
      const expiryDate = new Date(activeSubscription.end_date);
      const today = new Date();
      const daysUntilExpiry = Math.floor((expiryDate - today) / (1000 * 60 * 60 * 24));
      setIsSubscriptionExpiring(daysUntilExpiry <= 7); // Expiring in 7 days or less
    } else {
      setIsSubscriptionExpiring(false);
    }
  }, [activeSubscription]);

  // Fetch the user's current subscription
  const fetchCurrentSubscription = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCurrentSubscription();
      setActiveSubscription(data.length > 0 ? data[0] : null);
      if (data.length > 0) {
        setCurrentPlan(data[0].plan);
      }
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch subscription');
      setLoading(false);
    }
  };

  // Fetch all available plans
  const fetchAvailablePlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const plans = await getAvailablePlans();
      setAvailablePlans(plans);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch plans');
      setLoading(false);
    }
  };

  // Fetch subscription status (dashboard data)
  const fetchSubscriptionStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const status = await getSubscriptionStatus();
      setSubscriptionStatus(status);
      setLoading(false);
      return status;
    } catch (err) {
      setError('Failed to fetch subscription status');
      setLoading(false);
      throw err;
    }
  };

  // Subscribe to a new plan
  const subscribe = async (planId, couponCode = null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await subscribeToPlan(planId, couponCode);
      console.log('Subscription response:', response);
      await fetchCurrentSubscription();
      setLoading(false);
      return response;
    } catch (err) {
      setError('Failed to subscribe');
      setLoading(false);
      throw err;
    }
  };

  // Apply a coupon code
  const applyCoupon = async (code, planId) => {
    setLoading(true);
    setError(null);
    try {
      const data = await validateCoupon(code, planId);
      setCouponData(data);
      setLoading(false);
      return data;
    } catch (err) {
      setError('Invalid coupon code');
      setCouponData(null);
      setLoading(false);
      throw err;
    }
  };

  // Cancel subscription
  const cancelUserSubscription = async (subscriptionId) => {
    setLoading(true);
    setError(null);
    try {
      await cancelSubscription(subscriptionId);
      await fetchCurrentSubscription();
      setLoading(false);
    } catch (err) {
      setError('Failed to cancel subscription');
      setLoading(false);
      throw err;
    }
  };

  // Renew subscription
  const renewUserSubscription = async (subscriptionId) => {
    setLoading(true);
    setError(null);
    try {
      await renewSubscription(subscriptionId);
      await fetchCurrentSubscription();
      setLoading(false);
    } catch (err) {
      setError('Failed to renew subscription');
      setLoading(false);
      throw err;
    }
  };

  // Request a refund
  const requestSubscriptionRefund = async (subscriptionId, reason) => {
    setLoading(true);
    setError(null);
    try {
      await requestRefund(subscriptionId, reason);
      await fetchCurrentSubscription();
      setLoading(false);
    } catch (err) {
      setError('Failed to request refund');
      setLoading(false);
      throw err;
    }
  };

  // Fetch payment methods
  // const fetchPaymentMethods = async () => {
  //   setLoading(true);
  //   setError(null);
  //   try {
  //     const methods = await getPaymentMethods();
  //     setPaymentMethods(methods);
  //     setLoading(false);
  //   } catch (err) {
  //     setError('Failed to fetch payment methods');
  //     setLoading(false);
  //   }
  // };

  // Fetch subscription history
  const fetchSubscriptionHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const history = await getSubscriptionHistory();
      setSubscriptionHistory(history);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch subscription history');
      setLoading(false);
    }
  };

  // Upgrade to a new plan
  const upgradePlan = async (newPlanId) => {
    setLoading(true);
    setError(null);
    try {
      // If there's an active subscription, cancel it first
      if (activeSubscription) {
        await cancelSubscription(activeSubscription.id);
      }
      const response = await subscribe(newPlanId);
      setLoading(false);
      return response;
    } catch (err) {
      setError('Failed to upgrade plan');
      setLoading(false);
      throw err;
    }
  };

  // Toggle auto-renewal
  // const toggleAutoRenewal = async (subscriptionId, autoRenew) => {
  //   setLoading(true);
  //   setError(null);
  //   try {
  //     await updateSubscriptionAutoRenewal(subscriptionId, autoRenew);
  //     await fetchCurrentSubscription();
  //     setLoading(false);
  //   } catch (err) {
  //     setError('Failed to update auto-renewal settings');
  //     setLoading(false);
  //     throw err;
  //   }
  // };

  // Refresh all subscription data
  const refreshAllSubscriptionData = async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([
        fetchCurrentSubscription(),
        fetchAvailablePlans(),
        fetchSubscriptionHistory(),
        fetchSubscriptionStatus()
      ]);
      setLoading(false);
    } catch (err) {
      setError('Failed to refresh subscription data');
      setLoading(false);
    }
  };

  // Plan filtering utilities
  const getFreePlans = () => availablePlans.filter(plan => plan.subtype === 'free');
  const getPremiumPlans = () => availablePlans.filter(plan => plan.subtype === 'premium');
  const getTrialPlans = () => availablePlans.filter(plan => plan.subtype === 'trial');
  const getPlanById = (planId) => availablePlans.find(plan => plan.id === planId);

  // Plan comparison helper
  const comparePlanFeatures = async (planIds) => {
    setLoading(true);
    setError(null);
    try {
      const comparisonData = await comparePlans(planIds);
      setLoading(false);
      return comparisonData;
    } catch (err) {
      setError('Failed to compare plans');
      setLoading(false);
      throw err;
    }
  };

  // Clear error state
  const clearError = () => setError(null);

  // Context value
  const value = {
    currentPlan,
    activeSubscription,
    availablePlans,
    loading,
    error,
    couponData,
    isSubscriptionExpiring,
    // paymentMethods,
    subscriptionHistory,
    subscriptionStatus,
    
    // Core subscription operations
    fetchCurrentSubscription,
    fetchAvailablePlans,
    fetchSubscriptionStatus,
    subscribe,
    applyCoupon,
    cancelUserSubscription,
    renewUserSubscription,
    requestSubscriptionRefund,
    
    // Enhanced functionality
    // fetchPaymentMethods,
    fetchSubscriptionHistory,
    upgradePlan,
    // toggleAutoRenewal,
    refreshAllSubscriptionData,
    
    // Utility functions
    getFreePlans,
    getPremiumPlans,
    getTrialPlans,
    getPlanById,
    comparePlanFeatures,
    clearError
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionContext;